import { IconPlus, IconWorld } from "@tabler/icons-react"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router"

import Button from "~/components/Button"
import InputField from "~/components/InputField"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import withAccessRequirement from "~/hocs/withAccessRequirement"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import AutomaticDomain from "./AutomaticDomain"
import ManualDomain from "./ManualDomain"

const title = <PageBreadcrumb title="Custom Domain" path={[{ title: "Settings", link: routes.settings.index() }]} />

const DomainSettingsPage = withAccessRequirement("customDomain", title, () => {
	const user = useUser()
	const [searchParams] = useSearchParams()
	const alert = useAlert()
	const [completionTimedOut, setCompletionTimedOut] = useState(false)

	const { data: domains, error, refetch } = query.domain.list.useQuery()

	const [addDomainModal, setAddDomainModal] = useState<{
		isOpen: boolean
		state: "choose" | "manual"
		input: string
		isSubmittingManualDomain: boolean
	}>({ isOpen: false, state: "choose", input: "", isSubmittingManualDomain: false })

	const completedPurchaseId = (() => {
		const param = searchParams.get("purchase-completed")
		if (param === null) return null
		const number = parseInt(param)
		if (Number.isNaN(number)) return null
		return number
	})()

	const completedRenewal = (() => {
		const idParam = searchParams.get("renew-completed")
		const previousExpireDateParam = searchParams.get("previous-expire-date")
		if (idParam === null || previousExpireDateParam === null) return null

		const id = parseInt(idParam)
		const previousExpireDate = new Date(previousExpireDateParam)
		if (Number.isNaN(id) || Number.isNaN(previousExpireDate.getTime())) return null

		return { id, previousExpireDate }
	})()

	const isWaitingForPurchasedDomain =
		domains !== undefined && completedPurchaseId !== null && !domains.some(x => x.id === completedPurchaseId)
	const isWaitingForRenewedDomain =
		domains !== undefined &&
		completedRenewal !== null &&
		!domains.some(
			domain =>
				domain.type === "automatic" &&
				domain.id === completedRenewal.id &&
				domain.expireDate.getTime() > completedRenewal.previousExpireDate.getTime(),
		)
	const isDomainCompletionPending = isWaitingForPurchasedDomain || isWaitingForRenewedDomain
	const isWaitingForDomainCompletion = isDomainCompletionPending && !completionTimedOut

	useEffect(() => {
		if (!isWaitingForDomainCompletion) return

		const pollingTimer = setInterval(refetch, 2000)
		const timeoutTimer = setTimeout(() => setCompletionTimedOut(true), 60_000)

		return () => {
			clearInterval(pollingTimer)
			clearTimeout(timeoutTimer)
		}
	}, [isWaitingForDomainCompletion, refetch])

	const handleAddNewDomain = async () => {
		if (addDomainModal.input.trim().length === 0) return

		setAddDomainModal(current => ({ ...current, isSubmittingManualDomain: true }))
		try {
			const { status } = await trpc.domain.createManual.mutate({ domain: addDomainModal.input })
			if (status === "invalid-domain") {
				await alert.show(
					"Invalid domain",
					"The domain you entered is invalid. Make sure you only include the domain without any slashes.",
				)
				setAddDomainModal(current => ({ ...current, isSubmittingManualDomain: false }))
				return
			}
			await refetch()
			setAddDomainModal({ isOpen: false, state: "choose", input: "", isSubmittingManualDomain: false })
		} catch (error) {
			console.error(error)
			setAddDomainModal(current => ({ ...current, isSubmittingManualDomain: false }))
		}
		setAddDomainModal(current => ({ ...current, isSubmittingManualDomain: false }))
	}

	const closeAddDomainModal = () =>
		setAddDomainModal({ isOpen: false, input: "", state: "choose", isSubmittingManualDomain: false })

	return (
		<Layout>
			<DefaultModal
				isOpen={addDomainModal.isOpen}
				onClose={addDomainModal.isSubmittingManualDomain ? undefined : closeAddDomainModal}
			>
				<ModalTitle>Add a new domain</ModalTitle>
				{addDomainModal.state === "choose" ? (
					<>
						<ModalText>
							Do you want to register a domain with us or do you already have a domain you want to
							configure manually?
						</ModalText>
						<ModalButtons $vertical>
							<Button variant="primary" onClick={routes.settings.domain.register()}>
								I want to register a new domain
							</Button>
							<Button
								variant="secondary"
								onClick={() => setAddDomainModal(current => ({ ...current, state: "manual" }))}
							>
								I already have a domain
							</Button>
							<Button variant="secondary" onClick={closeAddDomainModal}>
								Cancel
							</Button>
						</ModalButtons>
					</>
				) : addDomainModal.state === "manual" ? (
					<>
						<ModalText>
							When adding an existing domain, you will need to manually configure your DNS settings.
						</ModalText>
						<ModalText>
							Your academy will be available at <strong>app.mydomain.com</strong>, but the subdomain can
							be changed at any time. If you have enabled the <strong>website</strong> feature, it will be
							available on <strong>mydomain.com</strong>.
						</ModalText>
						<ModalInputs>
							<InputField
								label="Domain"
								placeholder="mydomain.com"
								value={addDomainModal.input}
								onChange={value => setAddDomainModal(current => ({ ...current, input: value }))}
							/>
						</ModalInputs>
						<ModalButtons>
							<Button
								variant="secondary"
								onClick={() => setAddDomainModal(current => ({ ...current, state: "choose" }))}
								isDisabled={addDomainModal.isSubmittingManualDomain}
							>
								Back
							</Button>
							<Button
								variant="primary"
								onClick={handleAddNewDomain}
								isLoading={addDomainModal.isSubmittingManualDomain}
							>
								Add domain
							</Button>
						</ModalButtons>
					</>
				) : null}
			</DefaultModal>
			<div className="max-w-4xl pb-8">
				{title}
				<div className="border-component-border bg-component-background mb-5 flex flex-wrap items-center gap-4 rounded-2xl border px-5 py-4">
					<div className="bg-primary/10 text-primary flex size-11 shrink-0 items-center justify-center rounded-xl">
						<IconWorld size={24} stroke={1.6} aria-hidden="true" />
					</div>
					<div className="min-w-0 flex-1 basis-56">
						<h2 className="text-xl font-semibold tracking-tight">Your brand, your domain</h2>
						<p className="mt-1 text-sm text-gray-500">
							Give your academy a familiar address with a custom domain.
						</p>
					</div>
					<Button
						variant="primary"
						onClick={() => setAddDomainModal(current => ({ ...current, isOpen: true }))}
					>
						<IconPlus size={16} aria-hidden="true" /> Add domain
					</Button>
				</div>
				{isWaitingForDomainCompletion ? (
					<div
						role="status"
						className="border-component-border flex flex-col items-center rounded-2xl border bg-white p-6 text-sm text-gray-500"
					>
						<PartialLoadingPage />
						<p>
							{isWaitingForRenewedDomain
								? "Completing domain renewal..."
								: "Completing domain purchase..."}
						</p>
					</div>
				) : completionTimedOut && isDomainCompletionPending ? (
					<p>
						We could not complete the domain update. If fulfillment failed, the payment will be refunded
						automatically. Please contact support if the payment remains captured.
					</p>
				) : error ? (
					<div role="alert" className="border-component-border rounded-2xl border bg-white p-5 text-sm">
						<p>Failed to load domains.</p>
						<Button variant="secondary" onClick={() => refetch()} className="mt-3">
							Try again
						</Button>
					</div>
				) : domains === undefined ? (
					<PartialLoadingPage />
				) : (
					<>
						{domains.length === 0 ? (
							<div className="border-component-border rounded-2xl border bg-white px-6 py-10 text-center">
								<IconWorld
									className="text-primary mx-auto mb-4 size-9"
									stroke={1.4}
									aria-hidden="true"
								/>
								<h2 className="text-lg font-semibold">Find a home for your academy</h2>
								<p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-gray-500">
									Register a new domain through Forento, or connect one you already own by updating
									its DNS records.
								</p>
								<div className="mt-6 flex flex-wrap justify-center gap-3">
									<Button variant="primary" onClick={routes.settings.domain.register()}>
										Register a domain
									</Button>
									<Button
										variant="secondary"
										onClick={() =>
											setAddDomainModal(current => ({
												...current,
												isOpen: true,
												state: "manual",
											}))
										}
									>
										Connect existing domain
									</Button>
								</div>
							</div>
						) : (
							<div className="mb-3 flex items-center justify-between gap-3 px-1">
								<h2 className="text-sm font-semibold">
									Your domains <span className="ml-1 text-gray-400">{domains.length}</span>
								</h2>
								<p className="text-xs text-gray-500">Publish a domain to use it for your academy.</p>
							</div>
						)}
						<div className="space-y-4">
							{domains.map(domain =>
								domain.type === "automatic" ? (
									<AutomaticDomain
										key={`auto-${domain.id}`}
										domain={domain}
										reload={async () => {
											await user.reload()
											await refetch()
										}}
									/>
								) : (
									<ManualDomain
										key={`manual-${domain.id}`}
										domain={domain}
										reload={async () => {
											await user.reload()
											await refetch()
										}}
									/>
								),
							)}
						</div>
					</>
				)}
			</div>
		</Layout>
	)
})

export default DomainSettingsPage
